"use client";

import {
  Badge,
  Card,
  CardContent,
  FormCheckbox,
  FormEditableField,
  FormInputField,
  FormSelectField,
  Item,
  ItemContent,
  ItemDescription,
  ItemTitle,
  Label,
  type SelectOption,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import type { ICompanyApplicationTenant, IHubPackageDeployment } from "@models";
import { useHubSolutions } from "@query";
import { useMemo } from "react";
import { type Control, type UseFormSetValue, useWatch } from "react-hook-form";

/** Separator between `applicationId` and `tenantInstanceId` in form value. */
export const CATALOG_INSTALL_TENANT_KEY_SEP = "::";

/** Resolve display name and app map id from the composite `tenantKey` value (prefix is `appMapId`). */
export function resolveTenantFromKey(
  tenantKey: string,
  applicationTenants: ICompanyApplicationTenant[] | undefined,
): { applicationId: string; tenantName: string } | null {
  const sep = CATALOG_INSTALL_TENANT_KEY_SEP;
  const idx = tenantKey.indexOf(sep);
  if (idx < 0) return null;
  const appMapId = tenantKey.slice(0, idx).trim();
  const tenantInstanceId = tenantKey.slice(idx + sep.length);
  for (const app of applicationTenants ?? []) {
    if (app.appMapId?.trim() !== appMapId) continue;
    const instance = app.tenantInfo?.find(
      (ti) => ti.tenantInstanceId === tenantInstanceId,
    );
    if (instance) {
      return {
        applicationId: app.appMapId.trim(),
        tenantName:
          instance.tenantInstanceName?.trim() || instance.tenantInstanceId,
      };
    }
  }
  return null;
}

function buildTenantSelectOptions(
  data: ICompanyApplicationTenant[] | undefined,
): SelectOption[] {
  if (!data?.length) return [];
  const options: SelectOption[] = [];
  for (const app of data) {
    const appName = app.applicationName?.trim() || "";
    const fallback = app.applicationId || "";

    for (const tenant of app.tenantInfo ?? []) {
      const tenantName =
        tenant.tenantInstanceName?.trim() || tenant.tenantInstanceId || "";

      let displayLabel = fallback;
      if (appName && tenantName) {
        displayLabel = `${appName} ${tenantName}`;
      } else if (tenantName) {
        displayLabel = tenantName;
      } else if (appName) {
        displayLabel = appName;
      }

      options.push({
        value: `${app.appMapId}${CATALOG_INSTALL_TENANT_KEY_SEP}${tenant.tenantInstanceId}`,
        label: displayLabel,
      });
    }
  }
  return options;
}

export interface IPricingDeploymentStepProps {
  control: Control<IHubPackageDeployment>;
  setValue: UseFormSetValue<IHubPackageDeployment>;
  applicationTenants: ICompanyApplicationTenant[] | undefined;
  companyUuid?: string;
  numericSolutionCatalogId?: number;
}

export function PricingDeploymentStep({
  control,
  setValue,
  applicationTenants,
  companyUuid = "",
  numericSolutionCatalogId,
}: IPricingDeploymentStepProps) {
  const { t } = useTranslation();

  const applicationId = useWatch({
    control,
    name: "applicationId",
  });

  const nameValue = useWatch({
    control,
    name: "name",
  });

  const targetSolutionId = useWatch({
    control,
    name: "targetSolutionId",
  });

  const { data: hubSolutions, isLoading: hubSolutionsLoading } =
    useHubSolutions(companyUuid, applicationId ?? "", {
      enabled:
        !!companyUuid &&
        !!applicationId &&
        (numericSolutionCatalogId ?? 0) <= 0,
    });

  const activeHubSolutions = useMemo(
    () =>
      hubSolutions?.filter(
        (s) => !s.subscriptionStatus || s.subscriptionStatus === "ACTIVE",
      ) ?? [],
    [hubSolutions],
  );

  const solutionItems = useMemo(() => {
    return activeHubSolutions.map((solution) => {
      const title = solution.label || solution.name || solution.solutionId;
      const versionStr = Number.isInteger(solution.version)
        ? `v${solution.version}.0`
        : `v${solution.version}`;
      const label = versionStr ? `${title} - ${versionStr}` : title;

      return {
        value: solution.solutionId,
        label,
        description: solution.description,
      };
    });
  }, [activeHubSolutions]);

  const options = useMemo(
    () => buildTenantSelectOptions(applicationTenants),
    [applicationTenants],
  );

  const handleTenantChange = (value: string) => {
    const resolved = resolveTenantFromKey(value, applicationTenants);
    if (resolved) {
      setValue("tenantName", resolved.tenantName, { shouldValidate: true });
      setValue("applicationId", resolved.applicationId, {
        shouldValidate: true,
      });
      if ((numericSolutionCatalogId ?? 0) <= 0) {
        setValue("targetSolutionId", "", { shouldValidate: true });
      }
    } else {
      setValue("tenantName", "", { shouldValidate: true });
      setValue("applicationId", "", { shouldValidate: true });
      if ((numericSolutionCatalogId ?? 0) <= 0) {
        setValue("targetSolutionId", "", { shouldValidate: true });
      }
    }
  };

  if (!options.length) {
    return (
      <Label className="max-w-md text-sm font-normal text-muted-foreground">
        {t("hub.subscribe_wizard.tenants_no_company", {
          defaultValue:
            "Company context is required to load tenants. Try signing in again.",
        })}
      </Label>
    );
  }

  return (
    <Card
      variant="content"
      className="border-0 shadow-none"
      isLoading={!options.length}>
      <CardContent className="flex flex-col gap-4">
        <FormEditableField
          name="name"
          label={t("hub.subscribe_wizard.deployment_name", {
            defaultValue: "Name",
          })}
          value={nameValue}
          className="max-w-md">
          <FormInputField<IHubPackageDeployment>
            control={control}
            name="name"
            className="max-w-md"
            label={t("hub.subscribe_wizard.deployment_name", {
              defaultValue: "Name",
            })}
            description={t("hub.subscribe_wizard.deployment_name_desc", {
              defaultValue: "Enter a unique name for this deployment.",
            })}
            required
            placeholder={t("hub.subscribe_wizard.deployment_name_placeholder", {
              defaultValue: "Enter deployment name",
            })}
          />
        </FormEditableField>
        <FormSelectField<IHubPackageDeployment>
          control={control}
          name="tenantKey"
          required
          className="max-w-md"
          label={t("hub.subscribe_wizard.target_application", {
            defaultValue: "Target Application",
          })}
          description={t("hub.subscribe_wizard.target_application_desc", {
            defaultValue: "Select the target application for this deployment.",
          })}
          items={options}
          placeholder={t(
            "hub.subscribe_wizard.deployment_application_placeholder",
            {
              defaultValue: "Select an application",
            },
          )}
          emptyMessage={t("hub.subscribe_wizard.deployment_application_empty", {
            defaultValue: "No matching application.",
          })}
          onValueChange={handleTenantChange}
          enableSearch
        />

        {(numericSolutionCatalogId ?? 0) <= 0 && (
          <>
            <FormSelectField<IHubPackageDeployment>
              className="max-w-md"
              isLoading={hubSolutionsLoading}
              control={control}
              name="targetSolutionId"
              label={t("hub.subscribe_wizard.target_solution", {
                defaultValue: "Target Solution",
              })}
              description={t("hub.subscribe_wizard.target_solution_desc", {
                defaultValue: "Select the target solution for this deployment.",
              })}
              placeholder={t("hub.subscribe_wizard.select_target_solution", {
                defaultValue: "Select a target solution",
              })}
              items={solutionItems}
              enableSearch
              required
              disabled={!applicationId || hubSolutionsLoading}
              renderOption={(item) => {
                if (typeof item === "string") {
                  return <ItemTitle className="font-medium">{item}</ItemTitle>;
                }

                const solution = activeHubSolutions.find(
                  (s) => s.solutionId === item.value,
                );
                const title = solution
                  ? solution.label || solution.name || solution.solutionId
                  : item.label;
                const versionStr = solution
                  ? Number.isInteger(solution.version)
                    ? `v${solution.version}.0`
                    : `v${solution.version}`
                  : null;

                return (
                  <Item variant="default" size="xs">
                    <ItemContent>
                      <ItemTitle>
                        {title}
                        {versionStr && (
                          <Badge variant="mono" size="sm">
                            {versionStr}
                          </Badge>
                        )}
                      </ItemTitle>
                      {item.description && (
                        <ItemDescription
                          className="text-xs line-clamp-2 text-left"
                          title={item.description}>
                          {item.description}
                        </ItemDescription>
                      )}
                    </ItemContent>
                  </Item>
                );
              }}
            />
            {!!targetSolutionId && (
              <FormCheckbox<IHubPackageDeployment>
                control={control}
                name="replaceTargetEntity"
                label={t("hub.subscribe_wizard.replace_target_entity", {
                  defaultValue: "Replace existing target solution entities",
                })}
                description={t(
                  "hub.subscribe_wizard.replace_target_entity_desc",
                  {
                    defaultValue:
                      "If checked, the target solution entities will be replaced with this add-on entities.",
                  },
                )}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
